<template>
  <div>
    <v-card-title>
      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter"
      />
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
      :rows-per-page-items="[25,50,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr
          @click="toggleSelected(props.item)"
        >
          <td>
            {{ props.item.description }}
          </td>
          <td>
            {{ parseFloat(props.item.amount)|toCurrency }}
          </td>
          <td>
            {{ props.item.security_id }}
          </td>
          <td>
            {{ types()[props.item.type] }}
          </td>
          <td>
            <DateDisplay
              :date="props.item.expected_payout_date"
            />
          </td>
          <td>
            {{ getTotalPieces(props.item) }}
          </td>
          <td>
            <DateDisplay
              :date="props.item.created_at"
            />
          </td>
          <td class="text-xs">
            <v-tooltip
              v-if="props.item.security || isAutomaticCashbackPayout(props.item)"
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="error"
                  v-on="on"
                  @click.stop="() => showCancelDialog(props.item)"
                >
                  <v-icon>cancel</v-icon>
                </v-btn>
              </template>
              <span>Decline</span>
            </v-tooltip>
            <v-tooltip
              v-if="props.item.security || isAutomaticCashbackPayout(props.item)"
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="() => showDialog(props.item)"
                >
                  <v-icon>check</v-icon>
                </v-btn>
              </template>
              <span>Approve</span>
            </v-tooltip>
            <v-btn
              dark
              flat
              icon
              color="primary"
              @click.stop="() => downloadCsv(props.item.payout_id)"
            >
              <v-icon>download</v-icon>
            </v-btn>
            <v-chip
              v-if="props.item.errors"
              class="ma-2"
              color="red"
              text-color="white"
              @click.stop="toggleErrors(props.item.errors)"
            >
              Errors!
            </v-chip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="error"
                  v-on="on"
                  @click.stop="() => showDeleteDialog(props.item)"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <span>Delete Payout</span>
            </v-tooltip>
          </td>
        </tr>
        <tr v-if="props.item.security && displayErrors === props.item.errors">
          <td colspan="12">
            <ErrorDetails
              :errors="props.item.errors"
            />
          </td>
        </tr>
        <tr v-if="props.item.payout_id && selected === props.item.payout_id">
          <td colspan="11">
            <PayoutDetails
              :payout="props.item"
              :holders="props.item.holders"
              :show-holders="true"
              :show-holders-payback-information="true"
              :allow-to-delete-holders="true"
            />
          </td>
        </tr>
      </template>
    </v-data-table>

    <ApprovalsApproveDialog
      key="approve_dialog"
      :visible="isDialogActive()"
      :payout="currentPayout"
      @close="hideDialog()"
      @submit="onApprove()"
    />

    <ApprovalsCancelDialog
      key="decline_dialog"
      :visible="isCancelDialogActive()"
      :payout="currentPayout"
      @close="hideCancelDialog()"
      @submit="onCancel()"
    />

    <DeleteDialog
      key="delete_dialog"
      :visible="isDeleteDialogActive()"
      :payout="currentPayout"
      @close="hideDeleteDialog()"
      @submit="onDelete()"
    />

    <v-snackbar
      v-model="snackbarShow"
      :color="snackbarColor"
      :auto-height="true"
    >
      <ul v-if="snackbarErrors">
        <li
          v-for="snackbarError in snackbarErrors"
          :key="snackbarError.message"
        >
          {{ snackbarError.message }}
        </li>
      </ul>

      <div
        v-if="snackbarMessage"
      >
        {{ snackbarMessage }}
      </div>

      <v-btn
        color="primary"
        text
        @click="snackbarShow = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import ApprovalsApproveDialog from './dialogs/payout/ApprovalsApproveDialog';
import PayoutDetails from '@/components/payoutsAndTaxes/payout/shared/PayoutDetails';
import ApprovalsCancelDialog from './dialogs/payout/ApprovalsCancelDialog';
import ErrorDetails from '@/components/payoutsAndTaxes/payout/shared/ErrorDetails';
import DeleteDialog from '@/components/payoutsAndTaxes/payout/dialogs/payout/DeleteDialog';
import { downloadCsvFile } from '@/helpers';
import { downloadFromS3 } from '@/helpers/axiosS3Downloader';
import DateDisplay from '@/components/payoutsAndTaxes/payout/shared/DateDisplay';

export default {
  name: 'HolderApprovals',
  components: {
    ErrorDetails,
    PayoutDetails,
    ApprovalsApproveDialog,
    ApprovalsCancelDialog,
    DeleteDialog,
    DateDisplay,
  },
  data() {
    return {
      snackbarShow: false,
      snackbarMessage: '',
      snackbarColor: 'error',
      snackbarErrors: [],
      selected: 0,
      displayErrors: {},
      currentPayout: {},
      showContractId: null,
      isLoading: false,
      isShowing: false,
      approvalDialog: false,
      cancelDialog: false,
      deleteDialog: false,
      pagination: {
        rowsPerPage: 25,
      },
      search: '',
      headers: [
        { text: 'Payout', value: 'description' },
        { text: 'Amount', value: 'amount' },
        { text: 'Security ID', value: 'security_id' },
        { text: 'Type', value: 'type' },
        { text: 'Expected Payout Date', value: 'expected_payout_date' },
        { text: 'Total Pieces', value: 'total_pieces' },
        { text: 'Created At', value: 'created_at' },
        { text: 'Actions', value: 'actions' },
      ],
    };
  },
  computed: {
    payoutHolderPendingApprovals() {
      return this.$store.getters.getPayoutsHolderApprovalPending;
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.payoutHolderPendingApprovals :
        this.payoutHolderPendingApprovals.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  methods: {
    types() {
      const data = this.$store.getters.getPayoutsCreationTypes;
      if (!data || !data.payout || !data.payout.types) {
        return {};
      }

      return data.payout.types;
    },
    onApprove() {
      this.$store.dispatch('callPayoutHolderApproval', { payoutId: this.currentPayout.payout_id, approve: true })
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout Holders successfully approved';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    onCancel() {
      this.$store.dispatch('callPayoutHolderApproval', { payoutId: this.currentPayout.payout_id, approve: false })
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout Holders declined!';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    onDelete() {
      this.$store.dispatch('callPayoutDelete', this.currentPayout.payout_id)
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout Deleted!';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    isDeleteDialogActive() {
      return this.deleteDialog === true;
    },
    isAutomaticCashbackPayout(payout) {
      return payout.type === 'cashback_v2';
    },
    showDeleteDialog(item) {
      this.currentPayout = item;
      this.deleteDialog = true;
      this.$forceUpdate();
    },
    hideDeleteDialog() {
      this.deleteDialog = false;
      this.$forceUpdate();
    },
    isDialogActive() {
      return this.approvalDialog === true;
    },
    isCancelDialogActive() {
      return this.cancelDialog === true;
    },
    showDialog(item) {
      this.currentPayout = item;
      this.approvalDialog = true;
      this.$forceUpdate();
    },
    showCancelDialog(item) {
      this.currentPayout = item;
      this.cancelDialog = true;
      this.$forceUpdate();
    },
    hideDialog() {
      this.approvalDialog = false;
      this.$forceUpdate();
    },
    hideCancelDialog() {
      this.cancelDialog = false;
      this.$forceUpdate();
    },
    toggleSelected(payout) {
      this.selected = this.selected === payout.payout_id ? null : payout.payout_id;
      this.loadPayoutHolders(payout);
    },
    loadPayoutHolders(payout) {
      if (payout.holders && payout.holders.length !== 0) {
        return;
      }

      this.$store.dispatch('fetchPayoutDataGetHolders', {
        origin: 'holderApproval',
        payoutId: payout.payout_id,
      })
        .then(() => {
          this.$forceUpdate();
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    toggleErrors(errors) {
      this.displayErrors = this.displayErrors === errors ? null : errors;
    },
    getTotalPieces(payout) {
      if (!payout || !payout.holders || payout.holders.length === 0) {
        return 'Expand to calculate';
      }

      const initialSumValue = 0;

      return payout.holders.reduce(
        (sum, holder) => sum +
          holder.payback_information.interest_determination_base.details.pieces || 0,
        initialSumValue,
      );
    },
    downloadCsv(payoutId) {
      this.$store
        .dispatch('fetchPayoutHolderApprovalDownloadCsv', payoutId)
        .then((response) => {
          downloadFromS3(response.data).then((data) => {
            downloadCsvFile(data, response.data.fileName);
          });
        });
    },
  },
};
</script>
<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/HolderApprovals.vue